// /components/games/MathReflex.js
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  CssBaseline,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";

const MathReflex = () => {
  const [question, setQuestion] = useState({});
  const [options, setOptions] = useState([]);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(100);
  const [isPlaying, setIsPlaying] = useState(false);
  const [themeMode, setThemeMode] = useState("light");
  const timerRef = useRef(null);

  // Create light/dark theme
  const theme = createTheme({
    palette: {
      mode: themeMode,
      primary: { main: themeMode === "light" ? "#1976d2" : "#64b5f6" },
      secondary: { main: "#ff9800" },
      background: {
        default: themeMode === "light" ? "#f4f7fa" : "#0d1117",
        paper: themeMode === "light" ? "#fff" : "#161b22",
      },
    },
    shape: { borderRadius: 12 },
    typography: { fontFamily: "Inter, sans-serif" },
  });

  // Generate a new math question
  const generateQuestion = () => {
    const operations = ["+", "-", "×", "÷"];
    const op = operations[Math.floor(Math.random() * operations.length)];
    let a = Math.floor(Math.random() * 10) + 1;
    let b = Math.floor(Math.random() * 10) + 1;
    if (op === "÷") b = [1, 2, 5, 10][Math.floor(Math.random() * 4)];

    let correct;
    switch (op) {
      case "+": correct = a + b; break;
      case "-": correct = a - b; break;
      case "×": correct = a * b; break;
      case "÷": correct = parseFloat((a / b).toFixed(1)); break;
      default: correct = a + b;
    }

    const wrongAnswers = [];
    while (wrongAnswers.length < 3) {
      const random = correct + Math.floor(Math.random() * 10 - 5);
      if (random !== correct && !wrongAnswers.includes(random)) {
        wrongAnswers.push(random);
      }
    }

    const allOptions = [...wrongAnswers, correct].sort(() => Math.random() - 0.5);
    setQuestion({ a, b, op, correct });
    setOptions(allOptions);
    setTimeLeft(100);
  };

  // Handle user selection
  const handleSelect = (choice) => {
    if (!isPlaying) return;
    if (choice === question.correct) {
      setScore((s) => s + 1);
      generateQuestion();
    } else {
      stopGame();
    }
  };

  // Game start
  const startGame = () => {
    setIsPlaying(true);
    setScore(0);
    generateQuestion();
  };

  // Stop game
  const stopGame = () => {
    setIsPlaying(false);
    clearInterval(timerRef.current);
  };

  // Timer logic
  useEffect(() => {
    if (!isPlaying) return;
    timerRef.current = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 0) {
          stopGame();
          return 0;
        }
        return t - 1;
      });
    }, 100);
    return () => clearInterval(timerRef.current);
  }, [isPlaying]);

  // Difficulty scaling
  useEffect(() => {
    if (score % 5 === 0 && score !== 0) {
      setTimeLeft((t) => Math.max(t - 10, 40));
    }
  }, [score]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background:
            themeMode === "light"
              ? "linear-gradient(135deg,#f0f4ff,#c3d9ff)"
              : "linear-gradient(135deg,#0f2027,#203a43,#2c5364)",
          transition: "0.3s",
          py: 4,
        }}
      >
        <Card
          sx={{
            p: 3,
            borderRadius: 4,
            boxShadow: 8,
            width: 340,
            textAlign: "center",
          }}
        >
          <CardContent>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h5" fontWeight="bold">
                🧮 Math Reflex
              </Typography>
              <Button
                onClick={() =>
                  setThemeMode(themeMode === "light" ? "dark" : "light")
                }
              >
                {themeMode === "light" ? "Dark" : "Light"}
              </Button>
            </Box>

            <Typography
              variant="h4"
              sx={{
                mt: 4,
                mb: 2,
                fontWeight: "bold",
                color: "primary.main",
              }}
            >
              {isPlaying
                ? `${question.a} ${question.op} ${question.b}`
                : "Ready?"}
            </Typography>

            <LinearProgress
              variant="determinate"
              value={timeLeft}
              sx={{ height: 8, borderRadius: 4, mb: 3 }}
            />

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: 2,
              }}
            >
              {options.map((num, idx) => (
                <Button
                  key={idx}
                  variant="contained"
                  onClick={() => handleSelect(num)}
                  sx={{
                    py: 2,
                    borderRadius: 2,
                    fontSize: 18,
                    fontWeight: "bold",
                  }}
                  disabled={!isPlaying}
                >
                  {num}
                </Button>
              ))}
            </Box>

            <Typography sx={{ mt: 3 }}>Score: {score}</Typography>

            <Box sx={{ mt: 2 }}>
              {!isPlaying ? (
                <Button variant="contained" onClick={startGame}>
                  Start
                </Button>
              ) : (
                <Button variant="outlined" color="error" onClick={stopGame}>
                  Stop
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </ThemeProvider>
  );
};

export default MathReflex;
